import { useState, useCallback, useEffect, useMemo } from 'react'
import { useLocation } from 'wouter'
import { Card } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { Link } from 'wouter'
import {
	AlertCircle,
	Check,
	ChevronRight,
	Home,
	CreditCard,
	ChevronLeft
} from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import Header from '../components/Header'
import { useUser } from '@/hooks/use-user'
import { useGetActiveMemberships } from '@/hooks/use-getActiveMemberships'
import AvailableMembershipCard from '@/components/AvailableMembershipCard'
import { useAvailableMemberships } from '@/hooks/use-availableMemberships'
import { usePrices } from '@/hooks/use-prices'
import {
	IAvailableMembership,
	ICachedMembershipSelection,
	Membership
} from '@/constants'
import { capitalize, formatDate } from '@/utils/format'
import { apiFetch } from '@/utils'
import { IS_TESTNET } from '@/constants/walletConnectionConfig'

interface TaxCalculationResult {
	subtotal: number
	fees: Array<{
		type: string
		rate: number
		amount: number
		description: string
	}>
	total: number
}

interface EnhancedMembership extends IAvailableMembership {
	usdPrice: number
	sechPrice: number
	taxCalculation?: TaxCalculationResult
	totalSechPrice?: number
}

export default function MembershipPage() {
	const [isProcessing, setIsProcessing] = useState(false)
	const [selectedTier, setSelectedTier] = useState<number | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [taxCalculations, setTaxCalculations] = useState<{ [key: number]: TaxCalculationResult }>({})
	const [isCalculatingTaxes, setIsCalculatingTaxes] = useState(false)
	const [, navigate] = useLocation()
	const { toast } = useToast()
	const { walletAddress } = useUser()
	const { convertUsdToToken } = usePrices()
	const {
		activeMemberships,
		hasActiveMemberships,
		mostRecentActiveMembership
	} = useGetActiveMemberships(walletAddress)

	const { memberships, isLoading: isLoadingAvailableMemberships } =
		useAvailableMemberships()

	// Enhanced memberships with SECH pricing and tax calculations
	const membershipsWithSechPricing = useMemo(() => {
		return memberships.map(membership => {
			const usdPrice = Number(membership.price)
			const sechPrice = convertUsdToToken(usdPrice, 'sech')
			const taxCalculation = taxCalculations[membership.id]
			const totalSechPrice = taxCalculation ? convertUsdToToken(taxCalculation.total, 'sech') : sechPrice

			return {
				...membership,
				usdPrice,
				sechPrice,
				taxCalculation,
				totalSechPrice
			} as EnhancedMembership
		})
	}, [memberships, convertUsdToToken, taxCalculations])

	// Calculate taxes for all memberships
	useEffect(() => {
		if (memberships.length > 0) {
			const calculateTaxes = async () => {
				setIsCalculatingTaxes(true)
				const calculations: { [key: number]: TaxCalculationResult } = {}

				try {
					// Calculate taxes for all memberships in parallel
					const taxPromises = memberships.map(async (membership) => {
						try {
							const response = await apiFetch('/v1/taxes/calculate', {
								method: 'POST',
								headers: {
									'Content-Type': 'application/json',
								},
								body: JSON.stringify({
									amount: Number(membership.price),
									transactionType: 'membership'
								})
							})

							if (!response.ok) {
								throw new Error(`HTTP error! status: ${response.status}`)
							}

							const result = await response.json()
							if (result.success) {
								return { id: membership.id, data: result.data }
							} else {
								throw new Error(result.message || 'Tax calculation failed')
							}
						} catch (error) {
							console.error(`Failed to calculate taxes for membership ${membership.id}:`, error)
							// Return fallback calculation without taxes
							return {
								id: membership.id,
								data: {
									subtotal: Number(membership.price),
									fees: [],
									total: Number(membership.price)
								}
							}
						}
					})

					const results = await Promise.all(taxPromises)

					results.forEach(result => {
						if (result) {
							calculations[result.id] = result.data
						}
					})

				} catch (error) {
					console.error('Error calculating taxes:', error)
					toast({
						title: 'Tax Calculation Error',
						description: 'Unable to calculate taxes. Showing base prices.',
						variant: 'destructive'
					})

					// Create fallback calculations
					memberships.forEach(membership => {
						calculations[membership.id] = {
							subtotal: Number(membership.price),
							fees: [],
							total: Number(membership.price)
						}
					})
				} finally {
					setTaxCalculations(calculations)
					setIsCalculatingTaxes(false)
				}
			}

			calculateTaxes()
		}
	}, [memberships, toast])

	// Updated purchase handler with complete tax calculation data
	const handlePurchase = useCallback(async () => {
		if (!selectedTier || isProcessing) return

		try {
			setError(null)
			setIsProcessing(true)

			const tier = membershipsWithSechPricing.find((t) => t.id === selectedTier)
			if (!tier) {
				throw new Error('Invalid membership tier selected')
			}

			// Ensure we have tax calculation data
			if (!tier.taxCalculation) {
				throw new Error('Tax calculation not available. Please try again.')
			}

			// Updated cache data structure with tax calculation
			const cacheData: ICachedMembershipSelection = {
				...tier,
				// Store the tax calculation as required by the interface
				taxCalculation: tier.taxCalculation,
				timestamp: Date.now()
			}

			localStorage.setItem('selectedMembership', JSON.stringify(cacheData))

			toast({
				title: 'Membership Selected',
				description: `${tier.title} selected. Please complete the required waivers.`
			})

			// Navigate to waivers page
			navigate('/waivers')
		} catch (error) {
			const errorMessage =
				error instanceof Error
					? error.message
					: 'Failed to process membership selection'
			setError(errorMessage)
			toast({
				title: 'Selection Failed',
				description: errorMessage,
				variant: 'destructive'
			})
			localStorage.removeItem('selectedMembership')
			setSelectedTier(null)
		} finally {
			setIsProcessing(false)
		}
	}, [selectedTier, isProcessing, navigate, toast, membershipsWithSechPricing])

	useEffect(() => {
		!walletAddress && selectedTier && setSelectedTier(null)
	}, [walletAddress, selectedTier])

	const handleMembershipSelection = (item: IAvailableMembership) => {
		// Find the enhanced membership with tax data
		const enhancedItem = membershipsWithSechPricing.find(m => m.id === item.id)

		// Don't allow selection if taxes are still calculating
		if (isCalculatingTaxes || !enhancedItem?.taxCalculation) {
			return
		}

		if (selectedTier != item.id) {
			setSelectedTier(item.id)
		} else {
			handlePurchase()
		}
	}

	const canBuyMembershipToday = useMemo(() => {
		if (!hasActiveMemberships || !mostRecentActiveMembership) return true
		const now = Date.now()
		const membershipStartDate = new Date(
			mostRecentActiveMembership.startDate
		).getTime()
		const minAllowedDateToBuy = membershipStartDate + 24 * 60 * 60 * 1000

		return now >= minAllowedDateToBuy
	}, [hasActiveMemberships, mostRecentActiveMembership])

	const formatActiveMembershipName = (am: Membership) => {
		const availableMembership = memberships.find((m) => m.name === am.type)

		if (availableMembership) {
			return availableMembership.title
		}

		return capitalize(am.type)
	}

	const formatPrice = (amount: number) => {
		return Number(amount).toFixed(IS_TESTNET ? 6 : 2)
	}

	const TaxBreakdownCard = ({ membership }: { membership: EnhancedMembership }) => {
		if (!membership.taxCalculation) {
			return (
				<Card className="mt-4 p-4 bg-[#3C3C3C] border-0">
					<div className="flex items-center justify-center py-4">
						<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
						<span className="ml-2 text-sm text-[#BABABB]">Calculating taxes...</span>
					</div>
				</Card>
			)
		}

		const { taxCalculation } = membership

		return (
			<Card className="mt-4 p-4 bg-[#3C3C3C] border-0">
				<h4 className="font-semibold mb-2 flex items-center text-[#ffffff]">
					<Check className="h-4 w-4 mr-2 text-green-500" />
					Price Breakdown
				</h4>
				<div className="space-y-2 text-sm">
					<div className="flex justify-between">
						<span className="text-[#BABABB]">Subtotal:</span>
						<div className="text-right">
							<div className="text-[#ffffff]">${formatPrice(taxCalculation.subtotal)} USD</div>
							<div className="text-xs text-[#BABABB]">
								{formatPrice(convertUsdToToken(taxCalculation.subtotal, 'sech'))} SECH
							</div>
						</div>
					</div>
					{taxCalculation.fees.map((fee, index) => (
						<div key={index} className="flex justify-between text-[#BABABB]">
							<span>{fee.description} ({(fee.rate * 100).toFixed(1)}%):</span>
							<div className="text-right">
								<div>${formatPrice(fee.amount)} USD</div>
								<div className="text-xs">
									{formatPrice(convertUsdToToken(fee.amount, 'sech'))} SECH
								</div>
							</div>
						</div>
					))}
					<hr className="my-2 border-[#4C4C4C]" />
					<div className="flex justify-between font-semibold">
						<span className="text-[#ffffff]">Total:</span>
						<div className="text-right">
							<div className="text-[#ffffff]">${formatPrice(taxCalculation.total)} USD</div>
							<div className="text-sm text-primary font-semibold">
								{formatPrice(membership.totalSechPrice || 0)} SECH
							</div>
						</div>
					</div>
				</div>
			</Card>
		)
	}

	return (
		<div className="min-h-screen bg-[#18181B]">
			<div className="max-w-5xl mx-auto px-5 pb-8">
				{/* <nav className="mb-4 flex items-center space-x-1 text-sm text-muted-foreground">
                    <Link href="/" className="flex items-center hover:text-primary text-red-500">
                        <Home className="h-4 w-4" />
                        <span className="ml-1">Home</span>
                    </Link>
                    <ChevronRight className="h-4 w-4" />
                    <Link
                        href="/membership"
                        className="flex items-center text-foreground">
                        <CreditCard className="h-4 w-4" />
                        <span className="ml-1">Membership</span>
                    </Link>
                </nav> */}

				<div
					role="banner"
					className="h-12 flex items-center justify-center text-white mb-4 pt-3 relative"
				>
					<button
						type="button"
						aria-label="Go back"
						onClick={() => navigate('/')}
						className="absolute left-0
                        h-8 w-8
                        flex items-center justify-center
                        rounded-full
                        bg-white/10 hover:bg-white/20
                        focus:outline-none focus:ring-2 focus:ring-white/40"
					>
						<ChevronLeft className='h-[22px] w-[22px] text-[#ffffff]'/>
					</button>

					<h1 className="text-base font-semibold tracking-wide">
						Membership
					</h1>
				</div>


				<div className="text-start mb-4">
					<h1 className="text-[16px] leading-[18px] font-semibold font-figtree text-[#ffffff] mb-2">Choose Your Membership</h1>
					<p className="text-[13px] leading-[15px] font-regular text-[#BABABB]">
						Select a membership tier that suits your needs. All prices include applicable taxes.
					</p>
					{isCalculatingTaxes && (
						<p className="text-primary mt-2 flex justify-center items-center gap-2">
							<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
							Calculating taxes for all memberships...
						</p>
					)}
					{!canBuyMembershipToday && (
						<p className="text-yellow-500 mt-2 text-bold flex justify-center items-center gap-1 flex-col md:flex-row">
							<AlertCircle className="h-4 w-4" />
							You have already bought a membership recently. Please wait 24
							hours before buying a new one.
						</p>
					)}
				</div>

				{error && (
					<Alert variant="destructive" className="mb-6">
						<AlertCircle className="h-4 w-4" />
						<AlertDescription>{error}</AlertDescription>
					</Alert>
				)}

				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
					{membershipsWithSechPricing.map((item) => (
						<div key={item.id} className="flex flex-col">
							<AvailableMembershipCard
								onSelect={handleMembershipSelection}
								isCurrent={item.id === mostRecentActiveMembership?.id}
								isSelected={selectedTier === item.id}
								canBuyToday={canBuyMembershipToday && !isCalculatingTaxes}
								membership={{
									...item,
									price: item.taxCalculation ? item.taxCalculation.total : item.usdPrice,
									sechPrice: item.totalSechPrice || item.sechPrice
								}}
								isLoading={isCalculatingTaxes || !item.taxCalculation}
							/>
							{selectedTier === item.id && <TaxBreakdownCard membership={item} />}
						</div>
					))}
				</div>

				{hasActiveMemberships && (
					<div className="flex flex-col gap-2 mt-16 mb-16">
						<h1 className="text-xl font-bold text-[#ffffff]">Active Memberships:</h1>
						<div className="flex flex-col gap-2">
							{activeMemberships.map((ms) => (
								<Card key={ms.id} className="p-4 bg-[#3C3C3C] border-0">
									<div className="flex justify-between items-center">
										<div className="text-lg font-bold text-[#ffffff]">
											{formatActiveMembershipName(ms)}
										</div>
										<div className="text-[#BABABB]">
											{formatDate(ms.startDate)} - {formatDate(ms.endDate)}
										</div>
									</div>
								</Card>
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	)
}